"use client";

import { Button } from "uiShadcn/button";
import { Trash2 } from "lucide-react";
import { type ChangeEvent, forwardRef, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import FileStatus from "ui/FileInput/FileStatus";
import type { CvFileProps } from "ui/FileInput/types";
import { cn } from "utils/helpers/cn";
import { sanitizeFileName } from "utils/helpers/sanitizeFileName";

const FileInput = forwardRef<HTMLInputElement, CvFileProps>((props, ref) => {
    const {
        id,
        dataTestId,
        disabled,
        "aria-invalid": ariaInvalid,
        accept = ".doc,.pdf,.docx,.rtf,.txt,.xls,.ppt,.bmp,.gif,.jpg,.png,.odt,.odp,.ods,.xlsx,.pptx,.pages,.csv,.mp3",
        onChange,
        defaultFile,
        required,
        ...restProps
    } = props;

    const labelRef = useRef<HTMLLabelElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [file, setFile] = useState<File | null>(defaultFile || null);
    const hasError = !!ariaInvalid;
    const { formatMessage } = useIntl();

    const handleSetFile = (e: ChangeEvent<HTMLInputElement> | null) => {
        const selectedFile = e?.target?.files?.[0];
        const sanitizedFile = selectedFile ? sanitizeFileName(selectedFile) : null;

        setFile(sanitizedFile || null);

        if (onChange) {
            onChange(sanitizedFile || null);
        }

        if (labelRef.current) {
            labelRef.current.focus();
        }
    };

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDeleteFile = () => {
        handleSetFile(null);
        resetFileInput();
    };

    const openFileSelect = (e: KeyboardEvent<HTMLDivElement>) => {
        if (!fileInputRef || !fileInputRef.current) return;

        if (e.code === "Enter" || e.code === "Space") {
            e.preventDefault();
            fileInputRef.current.click();
        }
    };

    useEffect(() => {
        if (hasError && file) {
            setFile(null);
        }
    }, [hasError, file]);

    return (
        <div className="flex flex-col gap-2">
            <label
                ref={labelRef}
                htmlFor={id}
                className={cn(
                    "relative rounded-md focus-within:outline-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                    { "focus-within:ring-destructive": hasError },
                )}
            >
                <FileStatus
                    file={file}
                    isDragOver={isDragOver}
                    onKeyDown={openFileSelect}
                    disabled={disabled}
                    hasError={hasError}
                />
                <input
                    ref={(node) => {
                        if (typeof ref === "function") {
                            ref(node);
                        }
                        fileInputRef.current = node;
                    }}
                    id={id}
                    data-test-id={dataTestId}
                    type="file"
                    accept={accept}
                    disabled={disabled}
                    className={cn("absolute inset-0 cursor-pointer opacity-0", {
                        "cursor-not-allowed": disabled,
                    })}
                    tabIndex={-1}
                    onChange={handleSetFile}
                    onDragOver={() => {
                        setIsDragOver(true);
                    }}
                    onDragLeave={() => {
                        setIsDragOver(false);
                    }}
                    onDrop={() => {
                        setIsDragOver(false);
                    }}
                    aria-invalid={ariaInvalid}
                    required={required}
                    {...restProps}
                />
            </label>
            {file && (
                <Button
                    type="button"
                    className="!w-fit gap-2"
                    dataTestId={`remove-${dataTestId}`}
                    onClick={handleDeleteFile}
                    variant="ghost"
                    size="icon"
                    title={formatMessage({ id: "delete_file" })}
                >
                    <Trash2 size={20} />
                    {formatMessage({ id: "delete_file" })}
                </Button>
            )}
        </div>
    );
});

export default FileInput;
